import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  useLpAllocations,
  useReplaceLpAllocations,
  useResetLpAllocations,
} from '@/hooks/react-query/useLpAllocations';
import type { LpAllocationRow, LpAllocations } from '@/types/lpAllocation';
import type { APIError } from '@/utils/api-client';

import {
  addsUpToInvestment,
  draftAmount,
  draftFromRows,
  hasAnyError,
  validateLpAllocation,
  type LpAllocationDraft,
  type LpAllocationErrors,
} from './lpAllocationValidators';

export type EditorMode = 'VIEWING' | 'EDITING';

export interface LpAllocationEditor {
  allocationsQuery: UseQueryResult<LpAllocations, APIError>;
  allocations: LpAllocations | undefined;
  /** Every LP while editing; only those with an amount otherwise. */
  rows: LpAllocationRow[];
  draft: LpAllocationDraft;
  errors: LpAllocationErrors;
  isCustom: boolean;
  mode: EditorMode;
  isEditing: boolean;
  isTicked: boolean;
  isSaving: boolean;
  isReplacing: boolean;
  isOutOfStepWithInvestment: boolean;
  toggleCustom: (checked: boolean) => void;
  startEditing: () => void;
  cancel: () => void;
  save: () => void;
  setAmount: (relationshipId: string, amount: number | null) => void;
}

function allocationsVersion(allocations: LpAllocations): string {
  return [
    allocations.basis,
    allocations.is_editable,
    allocations.invested_amount,
    ...allocations.rows.map((row) => row.amount),
  ].join('|');
}

/** Shared by the checkbox in the transaction header and the table below verification. */
export function useLpAllocationEditor(
  fundId: string,
  fundTransactionId: string,
  enabled: boolean
): LpAllocationEditor {
  const allocationsQuery = useLpAllocations(fundId, fundTransactionId, enabled);
  const allocations = allocationsQuery.data;
  const replace = useReplaceLpAllocations(fundId, fundTransactionId);
  const reset = useResetLpAllocations(fundId, fundTransactionId);
  const [mode, setMode] = useState<EditorMode>('VIEWING');
  const [draft, setDraft] = useState<LpAllocationDraft>({});
  const [errors, setErrors] = useState<LpAllocationErrors>({});
  const [syncedVersion, setSyncedVersion] = useState<string | null>(null);

  // Every server change, approval included, restarts the editor from what was saved.
  const version = allocations ? allocationsVersion(allocations) : null;
  if (allocations && version !== syncedVersion) {
    setSyncedVersion(version);
    setDraft(draftFromRows(allocations.rows));
    setErrors({});
    setMode('VIEWING');
  }

  const isCustom = allocations?.basis === 'ALLOCATED';
  const isEditing = mode === 'EDITING';
  const allRows = allocations?.rows ?? [];

  const cancel = () => {
    setDraft(draftFromRows(allRows));
    setErrors({});
    setMode('VIEWING');
  };

  const save = () => {
    if (!allocations) return;
    const validationErrors = validateLpAllocation(
      allRows,
      draft,
      allocations.invested_amount
    );
    setErrors(validationErrors);
    if (hasAnyError(validationErrors)) return;
    replace.mutate(
      {
        allocations: allRows.map((row) => ({
          investor_fund_relationship_id: row.investor_fund_relationship_id,
          amount: draftAmount(draft, row),
        })),
      },
      { onSuccess: () => setMode('VIEWING') }
    );
  };

  // Unticking a saved custom split goes back to pro-rata; an unsaved one just closes.
  const toggleCustom = (checked: boolean) => {
    if (checked) {
      setMode('EDITING');
      return;
    }
    if (!isCustom) {
      cancel();
      return;
    }
    reset.mutate();
  };

  return {
    allocationsQuery,
    allocations,
    rows: isEditing ? allRows : allRows.filter((row) => row.amount > 0),
    draft,
    errors,
    isCustom,
    mode,
    isEditing,
    isTicked: isCustom || isEditing,
    isSaving: replace.isPending || reset.isPending,
    isReplacing: replace.isPending,
    isOutOfStepWithInvestment:
      !!allocations &&
      isCustom &&
      !isEditing &&
      !addsUpToInvestment(draftFromRows(allRows), allocations.invested_amount),
    toggleCustom,
    startEditing: () => setMode('EDITING'),
    cancel,
    save,
    setAmount: (relationshipId, amount) =>
      setDraft((current) => ({ ...current, [relationshipId]: amount })),
  };
}
